




























































































































































































































































































































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import SubscriptionService from '@/services/SubscriptionService';
import UserSubscriptionDetailsDTO from '@/dto/subscription/UserSubscriptionDetailsDTO';
import {processError} from "@/utils/ComponentUtils";
import {ChargeComponent} from "@/dto/subscription/SublimeChargeDTO";
import SubscriptionOffer from "./SubscriptionOffer.vue";
import AddProfilesQuoteDTO from '@/dto/subscription/quote/AddProfilesQuoteDTO';
import PortalAddress from "@/components/profile/branches/PortalAddress.vue";
import AuthService from '@/services/AuthService';
import Auth from '@/state/Auth';
import PolicyAcceptancePayload from '@/dto/auth/PolicyAcceptancePayload';
import PortalCheckbox from '@/components/new_style_components/PortalCheckbox.vue';
import Workspace, { WorkspaceType } from "@/dto/auth/Workspace";
import SubscriptionPlanService from '@/services/SubscriptionPlanService';
import Notifications from "@/state/Notifications";
import {SubscriberType} from "@/constants/SubscriptionConstants";

interface AvailableSubscription {
  groupName: string;
  name: string;
}

@Component({
  components: {
    SubscriptionOffer,
    PortalAddress,
    PortalCheckbox
  }
})
export default class MySubscriptions extends Vue {
  private subscriptions: UserSubscriptionDetailsDTO[] = [];
  private availableSubscriptions: AvailableSubscription[] = [];

  // Tab state
  private activeTab: 'offer' | 'actual' = 'offer';

  // Callback handling state
  private isProcessingCallback = false;
  private callbackAction: string | null = null;
  private callbackComponent: ChargeComponent | null = null;
  private pollInterval: number | null = null;

  // Company dropdown state
  private openDropdownIndex: number | null = null;

  // Policy modal state
  private showModalContent = false;
  private modalKey = 0;
  private termsAccepted = true;
  private privacyAccepted = true;
  private marketingConsent = true;
  private policyError: string | null = null;
  private pendingSubscription: { planId: number; companies: number[]; personId?: number | null } | null = null;

  // Add profiles modal state
  private showAddProfilesModalContent = false;
  private currentSubForAddition: UserSubscriptionDetailsDTO | null = null;
  private availableProfilesForAddition: Workspace[] = [];
  private selectedProfileMap: Record<string, boolean> = {};
  private pricingQuote: AddProfilesQuoteDTO | null = null;
  private isQuoteLoading = false;


  // Computed property to determine if tabs should be shown
  get shouldShowTabs(): boolean {
    // Show tabs only if there are BOTH offers and subscriptions
    return this.subscriptions.length > 0 && this.availableSubscriptions.length > 0;
  }

  get hasSelectedProfiles(): boolean {
    return Object.values(this.selectedProfileMap).some(v => v);
  }

  @Watch('selectedProfileMap', { deep: true })
  onSelectedProfilesChanged() {
    this.fetchPricingQuote();
  }

  private async fetchPricingQuote() {
    if (!this.currentSubForAddition || !this.hasSelectedProfiles) {
      this.pricingQuote = null;
      return;
    }

    const companyIds: number[] = [];
    let personId: number | null = null;

    Object.entries(this.selectedProfileMap).forEach(([key, selected]) => {
      if (!selected) return;

      const [type, idStr] = key.split('-');
      const id = parseInt(idStr, 10);

      if (type === WorkspaceType.COMPANY) {
        companyIds.push(id);
      } else if (type === WorkspaceType.PERSON) {
        personId = id;
      }
    });

    this.isQuoteLoading = true;
    try {
      const response = await SubscriptionService.getAddProfilesQuote(this.currentSubForAddition.agreementId, {
        personId,
        companyIds
      });
      this.pricingQuote = response.data;
    } catch (e) {
      console.error('Failed to fetch pricing quote:', e);
      this.pricingQuote = null;
    } finally {
      this.isQuoteLoading = false;
    }
  }

  async mounted() {
    // Check for callback parameters
    const action = this.$route.query.action as string;
    const sid = this.$route.query.sid as string;
    const component = this.$route.query.component as string;

    if (action && sid && component) {
      this.handleCallback(action, Number.parseInt(sid), component as ChargeComponent);
      return;
    }

    await this.loadSubscriptions();
    this.determineAvailableSubscriptions();
  }

  beforeDestroy() {
    // Clean up polling interval when component is destroyed
    if (this.pollInterval !== null) {
      clearInterval(this.pollInterval);
    }
  }

  private async loadSubscriptions() {
    this.$loadOn();
    return SubscriptionService.getMySubscriptions().then(
        ok => {
          this.$loadOff();
          this.subscriptions = ok.data;
          return Promise.resolve(ok);
        },
        err => {
          processError(err, this);
          return Promise.reject(err);
        }
    )
  }

  private async determineAvailableSubscriptions() {
    // Fetch available PayPal plan groups for current user from backend
    this.$loadOn();
    try {
      const resp = await SubscriptionPlanService.getMyActivePayPalGroups();
      const dicts = resp.data || [];
      // Build availableSubscriptions list from dictionaries
      this.availableSubscriptions = dicts.map(d => ({
        groupName: d.name,
        name: (this.$i18n.locale === 'en' ? (d.valueEn || d.name) : (d.valueRu || d.name))
      }));
    } catch (e) {
      // On error, keep availableSubscriptions empty and log
      console.error('Failed to load available subscription groups', e);
      this.availableSubscriptions = [];
    } finally {
      this.$loadOff();
    }

    // Auto-select appropriate tab based on what's available
    if (this.availableSubscriptions.length > 0 && this.subscriptions.length === 0) {
      this.activeTab = 'offer';
    } else if (this.subscriptions.length > 0 && this.availableSubscriptions.length === 0) {
      this.activeTab = 'actual';
    } else if (this.subscriptions.length > 0) {
      this.activeTab = 'offer';
    }
  }

  private async handleSubscribe(planId: number, companies: number[], personId?: number | null) {
    // Store subscription details and show policy modal
    this.pendingSubscription = { planId, companies, personId };
    this.resetPolicyForm();
    this.$modal.show('policy-acceptance-modal');
  }

  private resetPolicyForm() {
    this.termsAccepted = false;
    this.privacyAccepted = false;
    this.marketingConsent = false;
    this.policyError = null;
  }

  private onModalBeforeOpen() {
    // Force complete re-render of modal content
    this.showModalContent = false;
    this.$nextTick(() => {
      this.showModalContent = true;
      this.resetPolicyForm();
      // Clear any validation errors
      if (this.$validator) {
        this.$validator.reset();
      }
    });
  }

  private onModalBeforeClose() {
    this.showModalContent = false;
    this.resetPolicyForm();
  }

  private closePolicyModal() {
    this.$modal.hide('policy-acceptance-modal');
    this.pendingSubscription = null;
  }

  private async submitPolicyAndSubscribe() {
    // Manual validation as fallback since vee-validate has issues with modal scope
    // First try vee-validate
    try {
      const isValid = await this.$validator.validateAll('policyModal');
      console.log('Validation result:', isValid);
      console.log('Validator errors:', this.$validator.errors);
    } catch (e) {
      console.error('Validation error:', e);
    }

    // Manual check for required fields
    if (!this.termsAccepted || !this.privacyAccepted) {
      this.policyError = this.$t('auth.subscription_policy_modal.validation_error') as string;

      // Manually trigger validation on each field to show red state
      this.$validator.validate('modalTosCheck', this.termsAccepted);
      this.$validator.validate('modalPrivacyCheck', this.privacyAccepted);

      return;
    }

    if (!this.pendingSubscription) return;

    this.policyError = null;
    this.$loadOn();

    const policies = new PolicyAcceptancePayload(
      this.termsAccepted,
      this.privacyAccepted,
      this.marketingConsent,
      null
    );

    SubscriptionService.createPayPalSubscription(
      this.pendingSubscription.planId,
      this.pendingSubscription.companies,
      policies,
      this.pendingSubscription.personId
    ).then(
        ok => {
          this.$loadOff();
          this.closePolicyModal();
          window.location.href = ok.data;
        },
        err => {
          this.$loadOff();
          processError(err, this);
        }
    )
  }

  private isSingleComponent(subscription: UserSubscriptionDetailsDTO): boolean {
    return subscription.components.length === 1;
  }

  private formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  }

  private formatPriceCents(priceCents: number): string {
    return (priceCents / 100).toFixed(2);
  }

  private calculateSalesTax(priceCents: number, taxRatePercent: string): string {
    const price = priceCents / 100;
    const taxRate = parseFloat(taxRatePercent) / 100;
    const taxAmount = taxRate * price;
    return taxAmount.toFixed(2);
  }

  private getComponentDescription(commonent: ChargeComponent): string {
    const key = `subscriptions.description.${commonent}`;
    return this.$t(key) as string;
  }

  private getAllCompanies(subscription: UserSubscriptionDetailsDTO): string[] {
    // Return the companies array directly from the subscription
    return subscription.companies || [];
  }

  private getAllProfiles(subscription: UserSubscriptionDetailsDTO): Array<{ name: string; isPerson: boolean }> {
    const profiles: Array<{ name: string; isPerson: boolean }> = [];

    // Add person first if present
    if (subscription.personName) {
      profiles.push({ name: subscription.personName, isPerson: true });
    }

    // Add all companies
    if (subscription.companies && subscription.companies.length > 0) {
      subscription.companies.forEach(companyName => {
        profiles.push({ name: companyName, isPerson: false });
      });
    }

    return profiles;
  }

  private handleCallback(action: string, sid: number, component: ChargeComponent) {
    this.isProcessingCallback = true;
    this.callbackAction = action;
    this.callbackComponent = component;

    if (action === 'success') {
      // Start polling for permission updates every 30 seconds
      this.checkSubscriptionActivated(sid);

      // Then check every 30 seconds
      this.pollInterval = window.setInterval(() => {
        this.checkSubscriptionActivated(sid);
      }, 30000);
    }
    // For cancel, just display the message - no polling needed
  }

  private startPolling() {
    // Check immediately
  }

  private async checkSubscriptionActivated(sid: number) {
    await this.loadSubscriptions();
    const hasSubscription = this.subscriptions.some(s => s.agreementId === sid);
    if(hasSubscription) {
      await AuthService.refreshToken();
      if (this.pollInterval !== null) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
        this.determineAvailableSubscriptions();
        this.isProcessingCallback = false;
      }
    }
  }

  private async checkAccessUpdate() {
    try {
      await AuthService.refreshToken();

      // Check if access has been granted for the specific component
      const hasAccess = this.callbackComponent && Auth.hasDomainAccess(this.callbackComponent);

      if (hasAccess) {
        // Stop polling
        if (this.pollInterval !== null) {
          clearInterval(this.pollInterval);
          this.pollInterval = null;
        }

        // Reload subscriptions and update state
        await this.loadSubscriptions();
        this.determineAvailableSubscriptions();
        this.isProcessingCallback = false;
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      // Continue polling even if there's an error
    }
  }

  private retrySubscription() {
    // Reset callback state and reload the page to show subscription options
    this.isProcessingCallback = false;
    this.callbackAction = null;
    this.$router.push('/my-subscriptions');
  }

  private toggleCompanyDropdown(index: number) {
    // Toggle dropdown: if already open, close it; otherwise open it
    if (this.openDropdownIndex === index) {
      this.openDropdownIndex = null;
    } else {
      this.openDropdownIndex = index;
    }
  }

  private navigateToPostalRegister(companyName: string) {
    // Find company ID by name in Workspaces
    const workspaces = this.$wss.getAccessibleWorkspaces();
    console.log(companyName);
    console.log('workspaces', workspaces);
    const company = workspaces.find(
      ws => ws.type === WorkspaceType.COMPANY && ws.name === companyName
    );

    if (company) {
      // Navigate to postal register with companyId parameter
      this.$router.push({
        name: this.$routeNames.POSTREG_CUSTOMER,
        params: { companyId: company.id.toString() }
      });
    } else {
      console.error('Company not found in workspaces:', companyName);
    }

    // Close dropdown after navigation
    this.openDropdownIndex = null;
  }

  private async cancelRenewal(agreementId: number) {
    if (confirm('Are you sure you want to cancel the renewal?')) {
      this.$loadOn();
      SubscriptionService.cancelSubscriptionRenewal(agreementId).then(
        () => {
          this.loadSubscriptions().then(() => {
            this.$loadOff();
          });
        },
        err => {
          this.$loadOff();
          processError(err, this);
        }
      );
    }
  }

  private canAddProfiles(subscription: UserSubscriptionDetailsDTO): boolean {
    const allAccessibleWorkspaces = this.$wss.getAccessibleWorkspaces();
    const existingProfileNames = new Set(this.getAllProfiles(subscription).map(p => p.name));

    return allAccessibleWorkspaces.some(ws =>
            ws.type !== WorkspaceType.SUBLIME_EMPLOYEE && !existingProfileNames.has(ws.name)
            && (
                subscription.subscriberType === SubscriberType.PERSON && ws.type === WorkspaceType.PERSON
                || subscription.subscriberType === SubscriberType.COMPANY && ws.type === WorkspaceType.COMPANY
                || subscription.subscriberType === SubscriberType.ALL
            )
    );
  }

  private openAddProfilesModal(subscription: UserSubscriptionDetailsDTO) {
    this.currentSubForAddition = subscription;
    this.showAddProfilesModalContent = true;
    this.selectedProfileMap = {};

    // Filter available profiles
    const allAccessibleWorkspaces = this.$wss.getAccessibleWorkspaces();
    const existingProfileNames = new Set(this.getAllProfiles(subscription).map(p => p.name));

    this.availableProfilesForAddition = allAccessibleWorkspaces.filter(ws => {
      // Exclude profiles already in the subscription by name matching
      // Also ignore SUBLIME_EMPLOYEE type
      // Apply subscriberType restriction: PERSON -> only PERSON, COMPANY -> only COMPANY, ALL -> both
      const baseOk = ws.type !== WorkspaceType.SUBLIME_EMPLOYEE && !existingProfileNames.has(ws.name);
      if (!baseOk) return false;
      if (subscription.subscriberType === SubscriberType.PERSON) {
        return ws.type === WorkspaceType.PERSON;
      }
      if (subscription.subscriberType === SubscriberType.COMPANY) {
        return ws.type === WorkspaceType.COMPANY;
      }
      // SubscriberType.ALL
      return true;
    });

    this.$modal.show('add-profiles-modal');
  }

  private closeAddProfilesModal() {
    this.$modal.hide('add-profiles-modal');
    this.showAddProfilesModalContent = false;
    this.currentSubForAddition = null;
    this.selectedProfileMap = {};
  }

  private confirmAddProfiles() {
    if (!this.currentSubForAddition) return;

    const companyIds: number[] = [];
    let personId: number | null = null;

    Object.entries(this.selectedProfileMap).forEach(([key, selected]) => {
      if (!selected) return;

      const [type, idStr] = key.split('-');
      const id = parseInt(idStr, 10);

      if (type === WorkspaceType.COMPANY) {
        companyIds.push(id);
      } else if (type === WorkspaceType.PERSON) {
        personId = id;
      }
    });

    this.$loadOn();
    SubscriptionService.addProfilesToPayPalSubscription(this.currentSubForAddition.agreementId, {
      personId,
      companyIds
    }).then(
      ok => {
        this.$loadOff();
        this.closeAddProfilesModal();
        // Redirect to PayPal approval URL
        window.location.href = ok.data;
      },
      err => {
        this.$loadOff();
        processError(err, this);
      }
    );
  }
}
